import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Team } from '@/types/api';
import * as authApi from '@/api/auth';
import { setClientAuthToken } from '@/api/client';

interface AuthState {
  user: User | null;
  teams: Team[];
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isTeamsLoading: boolean;
  error: string | null;
}

interface AuthActions {
  setUser: (user: User | null) => void;
  setTeams: (teams: Team[]) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  fetchCurrentUser: () => Promise<void>;
  fetchTeams: () => Promise<void>;
  logout: () => Promise<void>;
  signIn: () => void;
  signInWithCredentials: (username: string, password: string) => Promise<void>;
  reset: () => void;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  teams: [],
  token: null,
  isAuthenticated: false,
  isLoading: true,
  isTeamsLoading: false,
  error: null,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUser: (user) =>
        set({ user, isAuthenticated: !!user }),

      setTeams: (teams) => set({ teams }),

      setToken: (token) => set({ token }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      fetchCurrentUser: async () => {
        try {
          set({ isLoading: true, error: null });
          const user = await authApi.getCurrentUser();
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (err) {
          const error = err as { status?: number; detail?: string };
          if (error.status === 401) {
            set({ user: null, isAuthenticated: false, isLoading: false });
          } else {
            set({ error: error.detail ?? 'Failed to fetch user', isLoading: false });
          }
        }
      },

      fetchTeams: async () => {
        try {
          set({ isTeamsLoading: true });
          const teams = await authApi.getTeams();
          set({ teams });
        } catch (err) {
          const error = err as { detail?: string };
          set({ error: error.detail ?? 'Failed to fetch teams' });
        } finally {
          set({ isTeamsLoading: false });
        }
      },

      logout: async () => {
        try {
          await authApi.logout();
        } finally {
          set({ token: null });
          get().reset();
          window.location.href = '/';
        }
      },

      signIn: () => {
        window.location.href = authApi.getGoogleAuthUrl();
      },

      signInWithCredentials: async (username, password) => {
        try {
          set({ error: null });
          const { token } = await authApi.loginWithCredentials(username, password);
          setClientAuthToken(token);
          set({ token });

          const [user, teams] = await Promise.all([
            authApi.getCurrentUser(),
            authApi.getTeams(),
          ]);

          set({
            user,
            teams,
            isAuthenticated: true,
            isLoading: false,
            isTeamsLoading: false,
            error: null,
          });
        } catch (err) {
          const error = err as { detail?: string };
          setClientAuthToken(null);
          set({
            user: null,
            teams: [],
            token: null,
            isAuthenticated: false,
            isLoading: false,
            isTeamsLoading: false,
            error: error.detail ?? 'Failed to sign in',
          });
          throw err;
        }
      },

      reset: () => set(initialState),
    }),
    {
      name: 'tc-insights-auth',
      partialize: (state) => ({
        // Persist auth state and token for cross-domain Bearer auth
        isAuthenticated: state.isAuthenticated,
        token: state.token,
      }),
    }
  )
);
